import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { motion, AnimatePresence } from "framer-motion";
import { FiLink, FiLoader, FiRefreshCw, FiShield, FiX } from "react-icons/fi";
import { useWallet } from "../hooks/useWallet";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { useTranslation } from "../hooks/useTranslation";
import { API_BASE, CONTRACT_ABIS, CONTRACT_ADDRESSES } from "../constants";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

export default function ReferralBindGateModal() {
    const { account, isConnected, isCorrectNetwork } = useWallet();
    const { executeWithFailover, getWalletSigner } = useRPCProvider();
    const { t } = useTranslation();
    const [status, setStatus] = useState("idle");
    const [referralAddress, setReferralAddress] = useState("");
    const [error, setError] = useState("");
    const [isDismissed, setIsDismissed] = useState(false);
    const requestRef = useRef(0);

    const tr = useCallback((key, fallback) => {
        const value = t(`referralGate.${key}`);
        return value === `referralGate.${key}` ? fallback : value;
    }, [t]);
    const trRef = useRef(tr);

    useEffect(() => {
        trRef.current = tr;
    }, [tr]);

    const checkReferral = useCallback(async () => {
        if (!account || !isConnected || !isCorrectNetwork) {
            setStatus("idle");
            setReferralAddress("");
            setError("");
            setIsDismissed(false);
            return;
        }

        const requestId = ++requestRef.current;
        setStatus("checking");
        setError("");
        setReferralAddress("");
        setIsDismissed(false);

        try {
            const result = await executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                const bound = await referralContract.isBindReferral(account);
                if (!bound) return { isBound: false, referralAddress: "" };

                const referral = await referralContract.getReferral(account);
                const address = String(referral || "");
                const isValid = ethers.isAddress(address)
                    && address.toLowerCase() !== ZERO_ADDRESS;

                return { isBound: isValid, referralAddress: isValid ? address : "" };
            });

            if (requestId !== requestRef.current) return;
            setStatus(result.isBound ? "bound" : "required");
        } catch (checkError) {
            if (requestId !== requestRef.current) return;
            console.error("Referral gate check failed:", checkError);
            setError(trRef.current("checkFailed", "Unable to check your referral status. Please retry."));
            setStatus("error");
        }
    }, [account, isConnected, isCorrectNetwork, executeWithFailover]);

    useEffect(() => {
        checkReferral();
    }, [checkReferral]);

    const validateReferral = useCallback(() => {
        const trimmed = referralAddress.trim();
        if (!ethers.isAddress(trimmed)) {
            return tr("invalidAddress", "Please enter a valid referral wallet address.");
        }
        if (trimmed.toLowerCase() === ZERO_ADDRESS) {
            return tr("invalidAddress", "Please enter a valid referral wallet address.");
        }
        if (account && trimmed.toLowerCase() === account.toLowerCase()) {
            return tr("selfReferral", "Your referral address cannot be your own wallet.");
        }
        return "";
    }, [account, referralAddress, tr]);

    const handleBind = async () => {
        const validationError = validateReferral();
        if (validationError) {
            setError(validationError);
            return;
        }

        const normalizedReferral = referralAddress.trim();
        setStatus("binding");
        setError("");

        try {
            const alreadyBound = await executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                return referralContract.isBindReferral(account);
            });

            if (alreadyBound) {
                await checkReferral();
                return;
            }

            const uniqueId = `${Date.now()}${Math.floor(Math.random() * 1000000)
                .toString()
                .padStart(6, "0")}`;
            const signResponse = await fetch(`${API_BASE}/publicApi/sign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    uniqueId,
                    callerAddress: account,
                    contractAddress: CONTRACT_ADDRESSES.REFERRALS,
                    userAddress: account,
                    referralAddress: normalizedReferral,
                }),
            });
            const signature = await signResponse.json().catch(() => ({}));
            if (!signResponse.ok || !signature?.success) {
                throw new Error(signature?.error || signature?.message || tr("signFailed", "Unable to prepare referral binding."));
            }

            const normalizeHex32 = (value) => {
                if (!value) return ethers.ZeroHash;
                const hex = String(value).trim();
                return hex.startsWith("0x") ? hex : `0x${hex}`;
            };
            const v = typeof signature.v === "string" ? parseInt(signature.v, 10) : Number(signature.v);
            if (!Number.isInteger(v)) {
                throw new Error(tr("signFailed", "Unable to prepare referral binding."));
            }

            const signer = await getWalletSigner();
            const referralContract = new ethers.Contract(
                CONTRACT_ADDRESSES.REFERRALS,
                CONTRACT_ABIS.REFERRALS,
                signer
            );
            const transaction = await referralContract.permitBindReferral(
                uniqueId,
                account,
                normalizedReferral,
                v,
                normalizeHex32(signature.r),
                normalizeHex32(signature.s)
            );
            const receipt = await transaction.wait();
            if (receipt?.status === 0) {
                throw new Error(tr("transactionFailed", "Referral binding transaction was reverted."));
            }

            await checkReferral();
        } catch (bindError) {
            console.error("Referral gate bind failed:", bindError);
            setError(bindError?.message || tr("bindFailed", "Unable to bind your referral. Please try again."));
            setStatus("required");
        }
    };

    const shouldShow = isConnected && isCorrectNetwork && status !== "bound" && status !== "idle" && !isDismissed;
    const isBusy = status === "checking" || status === "binding";
    const validationError = status === "required" ? validateReferral() : "";

    return (
        <AnimatePresence>
            {shouldShow && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="referral-gate-title">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-[#09060f]/90 backdrop-blur-sm"
                        onClick={() => setIsDismissed(true)}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 16 }}
                        transition={{ duration: 0.2 }}
                        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#9770DD]/40 bg-[#1a1023] shadow-2xl shadow-black/50"
                    >
                        <button
                            type="button"
                            onClick={() => setIsDismissed(true)}
                            className="absolute right-4 top-4 z-10 rounded-xl p-2 text-white/60 transition hover:bg-white/10 hover:text-white"
                            aria-label="Close"
                        >
                            <FiX size={20} />
                        </button>
                        <div className="border-b border-white/10 bg-gradient-to-r from-[#2a1743] to-[#1a1023] px-6 py-5 text-center">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#9770DD]/20 text-[#c7b5ff]">
                                <FiLink size={24} />
                            </div>
                            <h2 id="referral-gate-title" className="text-xl font-bold text-white">
                                {tr("title", "Complete Your Referral")}
                            </h2>
                            <p className="mt-2 text-sm leading-6 text-white/70">
                                {tr("description", "Please bind your referral wallet address to continue.")}
                            </p>
                        </div>

                        <div className="px-6 py-5">
                            {status === "checking" || status === "error" ? (
                                <div className="py-5 text-center">
                                    {status === "checking" ? (
                                        <>
                                            <FiLoader className="mx-auto mb-3 animate-spin text-[#b79bff]" size={26} />
                                            <p className="text-sm text-white/80">{tr("checking", "Checking your referral status...")}</p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm text-red-200">{error}</p>
                                            <button type="button" onClick={checkReferral} className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#9770DD]/50 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10">
                                                <FiRefreshCw size={15} /> {tr("retry", "Retry")}
                                            </button>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <label htmlFor="referral-gate-address" className="mb-2 block text-sm font-semibold text-white">
                                        {tr("addressLabel", "Referral Address")}
                                    </label>
                                    <input
                                        id="referral-gate-address"
                                        value={referralAddress}
                                        onChange={(event) => { setReferralAddress(event.target.value); setError(""); }}
                                        placeholder={tr("placeholder", "0x...")}
                                        autoComplete="off"
                                        spellCheck="false"
                                        disabled={isBusy}
                                        className="w-full rounded-xl border border-white/15 bg-[#0f0918] px-4 py-3 font-mono text-sm text-white outline-none transition focus:border-[#a98cf7] focus:ring-2 focus:ring-[#9770DD]/30 disabled:opacity-60"
                                    />
                                    <div className="mt-3 flex gap-2 rounded-xl border border-amber-300/15 bg-amber-300/5 px-3 py-3 text-xs leading-5 text-amber-100/90">
                                        <FiShield className="mt-0.5 shrink-0" size={15} />
                                        <span>{tr("irreversible", "This referral binding is permanent and cannot be changed later.")}</span>
                                    </div>
                                    {(error || validationError) && <p className="mt-3 text-sm text-red-200">{error || validationError}</p>}
                                    <button
                                        type="button"
                                        onClick={handleBind}
                                        disabled={isBusy || !!validationError}
                                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#9770DD] px-4 py-3 font-semibold text-white transition hover:bg-[#7f5ad4] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isBusy && <FiLoader className="animate-spin" size={17} />}
                                        {status === "binding" ? tr("binding", "Binding Referral...") : tr("bind", "Bind Referral")}
                                    </button>
                                    <p className="mt-3 text-center text-xs text-white/45">
                                        {tr("walletConfirm", "You will confirm this request in your wallet.")}
                                    </p>
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
